import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Bell, RadioTower, Route, Settings2 } from 'lucide-react'
import { api } from '@/util/api'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { BackLink, Button, Notice, Tabs } from '@/components/play'
import { EventGeneralForm } from '@/components/EventGeneralForm'
import { EventCourseForm } from '@/components/EventCourseForm'
import { TrackingProvidersPanel } from '@/components/athletes/TrackingProvidersPanel'
import { EventPreShowSettings } from '@/components/settings/EventPreShowSettings'
import { WorkspaceHeader } from '@/components/workspace/WorkspaceHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { workspaceError } from '@/lib/workspace'
import type { EventResource } from '@/types/events'
import '@/components/workspace/workspace.css'
import '@/components/settings/event-settings.css'

const TABS = ['general', 'course', 'tracking', 'pre-show'] as const
type Tab = (typeof TABS)[number]

export function EventSettingsPage() {
  const { id } = useParams<{ id: string }>()
  return id ? <EventSettings key={id} eventId={id} /> : null
}

function EventSettings({ eventId }: { eventId: string }) {
  const [event, setEvent] = useState<EventResource | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  // The open tab is in the address (?tab=tracking), so another page can link straight to it.
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: Tab = TABS.find((each) => each === requested) ?? 'general'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<{ data: EventResource }>(`/events/${eventId}`)
      .then((res) => { if (!cancelled) setEvent(res.data) })
      .catch((failure) => {
        if (!cancelled) setError(workspaceError(failure, 'Your event settings couldn’t load. Try again.'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [eventId, attempt])

  return (
    <div className="play-theme pw-workspace ps-page">
      <WorkspaceHeader />
      <main className="ps-content">
        {/* Named for the event, so it also says whose settings these are. */}
        <BackLink to={`/events/${eventId}`} className="pw-back">{event?.attributes.name ?? 'Your event'}</BackLink>
        <header className="ps-heading">
          <h1>Settings</h1>
        </header>
        {loading ? (
          <ContentSkeleton label="Loading settings" variant="form" />
        ) : error ? (
          <Notice
            tone="error"
            alert
            action={<Button onClick={() => setAttempt((current) => current + 1)}>Try again</Button>}
          >
            {error}
          </Notice>
        ) : event ? (
          <Tabs
            label="Event settings"
            iconOnly
            value={tab}
            onValueChange={(next) => setParams(next === 'general' ? {} : { tab: next }, { replace: true })}
            items={[
              {
                value: 'general',
                label: 'General',
                icon: <Settings2 />,
                // Keep unsaved General edits while the Course tab is open.
                keepMounted: true,
                content: (
                  <div className="pt-8">
                    <EventGeneralForm eventId={eventId} event={event} onSaved={setEvent} />
                  </div>
                ),
              },
              {
                value: 'course',
                label: 'Course',
                icon: <Route />,
                content: (
                  <section className="ps-section pt-8" aria-labelledby="course-heading">
                    <h2 id="course-heading">Course</h2>
                    <EventCourseForm eventId={eventId} />
                  </section>
                ),
              },
              {
                value: 'tracking',
                label: 'Tracking',
                icon: <RadioTower />,
                content: (
                  <section className="ps-section pt-8" aria-labelledby="tracking-heading">
                    <h2 id="tracking-heading">Tracking providers</h2>
                    <TrackingProvidersPanel eventId={eventId} />
                  </section>
                ),
              },
              {
                value: 'pre-show',
                label: 'Pre-show',
                icon: <Bell />,
                content: (
                  <section className="ps-section pt-8" aria-labelledby="pre-show-heading">
                    <h2 id="pre-show-heading">Pre-show</h2>
                    <EventPreShowSettings eventId={eventId} event={event} onSaved={setEvent} />
                  </section>
                ),
              },
            ]}
          />
        ) : null}
      </main>
      <SiteFooter />
    </div>
  )
}
